import { useEffect, useRef } from 'react';
import {
  Canvas,
  FabricImage,
  FabricText,
  Group,
  type FabricObject,
} from 'fabric';
import type { SpotDetailDto } from '@parking/shared';
import vehicleTopUrl from '../../assets/spot-vehicle-top.png';

export type SpotMapPose = {
  id: string;
  mapX: number;
  mapY: number;
  mapRotation: number;
  mapScale: number;
};

type Props = {
  spots: SpotDetailDto[];
  floorPlanUrl: string | null | undefined;
  canEdit: boolean;
  emptyHint?: string;
  onPoseChange: (pose: SpotMapPose) => void;
  onPlaceUnplaced: (mapX: number, mapY: number) => void;
};

/** Target on-canvas height for the vehicle sprite at scale 1. */
const CAR_DISPLAY_H = 72;
const LABEL_GAP = 4;
const DEFAULT_LABEL_COLOR = '#FFFFFF';

function vehicleOpacity(spot: SpotDetailDto): number {
  const occupied = spot.status === 'OCCUPIED' || spot.sensorOccupied === true;
  return occupied ? 1 : 0.5;
}

function labelFill(spot: SpotDetailDto): string {
  if (spot.color && /^#[0-9A-Fa-f]{6}$/i.test(spot.color)) return spot.color;
  return DEFAULT_LABEL_COLOR;
}

async function buildVehicleGroup(
  spot: SpotDetailDto,
  canvasW: number,
  canvasH: number,
): Promise<Group> {
  const car = await FabricImage.fromURL(vehicleTopUrl);
  const natH = car.height || CAR_DISPLAY_H;
  const baseScale = CAR_DISPLAY_H / natH;
  car.set({
    originX: 'center',
    originY: 'center',
    left: 0,
    top: 0,
    scaleX: baseScale,
    scaleY: baseScale,
    opacity: vehicleOpacity(spot),
    objectCaching: false,
  });

  const label = new FabricText(spot.code, {
    fontSize: 11,
    fontFamily: 'Poppins, sans-serif',
    fontWeight: '600',
    fill: labelFill(spot),
    originX: 'center',
    originY: 'top',
    left: 0,
    top: CAR_DISPLAY_H / 2 + LABEL_GAP,
    textAlign: 'center',
    opacity: 1,
    objectCaching: false,
  });

  const group = new Group([car, label], {
    left: ((spot.mapX ?? 50) / 100) * canvasW,
    top: ((spot.mapY ?? 50) / 100) * canvasH,
    originX: 'center',
    originY: 'center',
    angle: spot.mapRotation ?? 0,
    scaleX: spot.mapScale ?? 1,
    scaleY: spot.mapScale ?? 1,
    hasControls: true,
    hasBorders: true,
    lockScalingFlip: true,
    transparentCorners: false,
    cornerColor: '#009EF7',
    cornerStrokeColor: '#ffffff',
    cornerStyle: 'circle',
    cornerSize: 6,
    touchCornerSize: 14,
    borderColor: '#009EF7',
    borderScaleFactor: 1,
    padding: 1,
    objectCaching: false,
  });

  group.set('spotId', spot.id);
  group.setControlsVisibility({
    mt: false,
    mb: false,
    ml: false,
    mr: false,
    mtr: true,
  });

  return group;
}

/**
 * Fabric.js floor-plan canvas: background image + photorealistic top-down
 * vehicle icons (full color when occupied, 50% opacity when free).
 */
export function SpotFloorPlanCanvas({
  spots,
  floorPlanUrl,
  canEdit,
  emptyHint,
  onPoseChange,
  onPlaceUnplaced,
}: Props) {
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const saveTimer = useRef<number | null>(null);
  const onPoseRef = useRef(onPoseChange);
  const onPlaceRef = useRef(onPlaceUnplaced);
  onPoseRef.current = onPoseChange;
  onPlaceRef.current = onPlaceUnplaced;

  useEffect(() => {
    if (!canvasElRef.current || !wrapRef.current) return;

    const width = wrapRef.current.clientWidth || 900;
    const height = Math.max(320, Math.round(width * 0.5));
    const canvas = new Canvas(canvasElRef.current, {
      width,
      height,
      backgroundColor: '#fafbfd',
      selection: canEdit,
      preserveObjectStacking: true,
      uniformScaling: true,
      targetFindTolerance: 6,
    });
    fabricRef.current = canvas;

    const scheduleSave = (obj: FabricObject) => {
      const spotId = obj.get('spotId') as string | undefined;
      if (!spotId) return;
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        const w = canvas.getWidth() || 1;
        const h = canvas.getHeight() || 1;
        const mapX = Math.min(100, Math.max(0, Math.round(((obj.left ?? 0) / w) * 1000) / 10));
        const mapY = Math.min(100, Math.max(0, Math.round(((obj.top ?? 0) / h) * 1000) / 10));
        const mapRotation = Math.round((obj.angle ?? 0) * 10) / 10;
        const mapScale = Math.round(((obj.scaleX ?? 1) + (obj.scaleY ?? 1)) * 50) / 100;
        onPoseRef.current({ id: spotId, mapX, mapY, mapRotation, mapScale });
      }, 250);
    };

    const onModified = (e: { target?: FabricObject }) => {
      if (e.target) scheduleSave(e.target);
    };
    canvas.on('object:modified', onModified);

    const onCanvasClick = (opt: { target?: FabricObject | null; e?: Event }) => {
      if (!canEdit || opt.target) return;
      const evt = opt.e as MouseEvent | undefined;
      if (!evt) return;
      const p = canvas.getScenePoint(evt);
      const w = canvas.getWidth() || 1;
      const h = canvas.getHeight() || 1;
      const mapX = Math.min(100, Math.max(0, Math.round((p.x / w) * 1000) / 10));
      const mapY = Math.min(100, Math.max(0, Math.round((p.y / h) * 1000) / 10));
      onPlaceRef.current(mapX, mapY);
    };
    canvas.on('mouse:down', onCanvasClick);

    const ro = new ResizeObserver(() => {
      if (!wrapRef.current || !fabricRef.current) return;
      const nextW = wrapRef.current.clientWidth || 900;
      const canvas = fabricRef.current;
      const prevW = canvas.getWidth() || nextW;
      const prevH = canvas.getHeight() || nextW;
      const bg = canvas.backgroundImage as FabricImage | undefined;
      let nextH = Math.max(320, Math.round(nextW * 0.5));
      if (bg && bg.width && bg.height) {
        nextH = Math.max(200, Math.round(nextW * (bg.height / bg.width)));
        const scale = nextW / bg.width;
        bg.set({
          scaleX: scale,
          scaleY: scale,
          left: 0,
          top: 0,
        });
      }
      canvas.setDimensions({ width: nextW, height: nextH });
      canvas.getObjects().forEach((obj) => {
        if (!obj.get('spotId')) return;
        obj.set({
          left: ((obj.left ?? 0) / prevW) * nextW,
          top: ((obj.top ?? 0) / prevH) * nextH,
        });
        obj.setCoords();
      });
      canvas.requestRenderAll();
    });
    ro.observe(wrapRef.current);

    return () => {
      ro.disconnect();
      canvas.off('object:modified', onModified);
      canvas.off('mouse:down', onCanvasClick);
      canvas.dispose();
      fabricRef.current = null;
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [canEdit]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    let cancelled = false;

    const paint = async () => {
      const wrapW = wrapRef.current?.clientWidth || canvas.getWidth() || 900;
      let w = wrapW;
      let h = canvas.getHeight() || Math.max(320, Math.round(w * 0.5));

      canvas.getObjects().forEach((o) => canvas.remove(o));

      if (floorPlanUrl) {
        try {
          const img = await FabricImage.fromURL(floorPlanUrl, { crossOrigin: 'anonymous' });
          if (cancelled) return;
          const iw = img.width || w;
          const ih = img.height || h;
          // Fill the full card width; canvas height follows image aspect ratio.
          const scale = w / iw;
          h = Math.max(200, Math.round(ih * scale));
          canvas.setDimensions({ width: w, height: h });
          img.set({
            originX: 'left',
            originY: 'top',
            scaleX: scale,
            scaleY: scale,
            left: 0,
            top: 0,
            selectable: false,
            evented: false,
          });
          canvas.backgroundImage = img;
          canvas.backgroundColor = '#1e1e2d';
        } catch {
          canvas.setDimensions({ width: w, height: h });
          canvas.backgroundImage = undefined;
          canvas.backgroundColor = '#fafbfd';
        }
      } else {
        h = Math.max(320, Math.round(w * 0.5));
        canvas.setDimensions({ width: w, height: h });
        canvas.backgroundImage = undefined;
        canvas.backgroundColor = '#fafbfd';
      }

      w = canvas.getWidth() || w;
      h = canvas.getHeight() || h;

      const placed = spots.filter((s) => s.mapX != null && s.mapY != null);
      for (const spot of placed) {
        try {
          const group = await buildVehicleGroup(spot, w, h);
          if (cancelled) return;
          group.selectable = canEdit;
          group.evented = canEdit;
          canvas.add(group);
        } catch {
          // skip spot if sprite fails to load
        }
      }
      canvas.requestRenderAll();
    };

    void paint();
    return () => {
      cancelled = true;
    };
  }, [spots, floorPlanUrl, canEdit]);

  return (
    <div className="floor-plan-canvas" ref={wrapRef}>
      <canvas ref={canvasElRef} />
      {!floorPlanUrl && emptyHint && (
        <div className="floor-plan-canvas__hint">{emptyHint}</div>
      )}
    </div>
  );
}
